import { t } from '@lingui/macro'
import { isNil } from 'ramda'

import { mixpanel } from '@/mixpanel'
import { getEncounterSpecialisation } from '@/modules/encounter/utils/encounter-mapping'
import { theme } from '@/theme-generated'
import { formatToWaitTime } from '@/utils/time'

import type { NavigateFunction } from 'react-router-dom'
import type { Appointment } from '@/modules/appointment/types/appointment.types'
import type { Encounter } from '@/modules/encounter/types/encounter.types'
import type { ActivityCardDescriptionProps } from '@/modules/shared/components/activity-card/ActivityCard.types'
import type { TimelineActivity } from '../Timeline'

function mapAppointmentsToTimeline(appointments?: Appointment[], navigate?: NavigateFunction, related = false) {
  if (isNil(appointments) || !Array.isArray(appointments)) {
    return []
  }

  const { pathname } = window.location
  const loc = pathname.split('/')[1]

  return appointments?.map((appointment) => {
    const start = appointment?.start
    const waitTime = appointment?.meta?.synops?.waitTime

    const location = appointment?.meta?.location?.name || appointment?.meta?.esign?.location?.name?.nl
    const specialisation = appointment?.meta?.esign?.specialisation?.title?.nl
    const doctor = appointment?.meta?.esign?.doctor?.name
    const noShow = appointment?.meta?.noShow

    const description: ActivityCardDescriptionProps[] = [
      {
        type: 'BodyMediumRegular',
        text: doctor,
      },
      {
        type: 'BodyMediumRegular',
        text: specialisation,
      },
      {
        type: 'BodyMediumRegular',
        text: location,
      },
    ]

    if (noShow) {
      description.push({
        type: 'BodyMediumBold',
        text: t({
          id: 'timeline.activity-card.no-show',
          message: `Je was niet op je afspraak`,
        }),
        color: theme.Colors.Status.Error,
      })
    }

    return {
      id: appointment.id,
      type: 'Appointment',
      description,
      start,
      scheduledTime: new Date(start),
      showStart: !appointment.meta?.hiddenInApp,
      waitTime: waitTime ? formatToWaitTime(waitTime) : undefined,
      carepath: appointment?.activities?.at(0)?.careplan?.display,
      onClick: () => {
        if (navigate) {
          mixpanel.track(`${loc}_${related ? 'related_' : ''}appointment_open`)
          navigate(`/timeline/appointment/${appointment.id}`)
        }
      },
    }
  }) as TimelineActivity[]
}

function mapEncountersToTimeline(encounters?: Encounter[], navigate?: NavigateFunction, related = false) {
  if (isNil(encounters) || !Array.isArray(encounters)) {
    return []
  }

  const { pathname } = window.location
  const location = pathname.split('/')[1]

  return encounters
    ?.filter((encounter) => encounter?.start)
    ?.map((encounter) => {
      return {
        id: encounter.id,
        type: 'Intake',
        description: [
          {
            type: 'BodyMediumRegular',
            text: getEncounterSpecialisation(encounter),
          },
          {
            type: 'BodyMediumRegular',
            text: encounter.meta?.location?.name,
          },
        ],
        start: encounter.start,
        scheduledTime: new Date(encounter.start),
        showStart:
          !encounter.meta?.hiddenInApp &&
          !(new Date(encounter.start).getHours() === 0 && new Date(encounter.start).getMinutes() === 0),
        onClick: () => {
          if (navigate) {
            mixpanel.track(`${location}_${related ? 'related_' : ''}encounter_open`)
            navigate(`/timeline/encounter/${encounter.id}`)
          }
        },
      }
    }) as TimelineActivity[]
}

export { mapAppointmentsToTimeline, mapEncountersToTimeline }
